import {AsyncPipe, TitleCasePipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {MatAnchor, MatButton, MatIconButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatOption} from '@angular/material/core';
import {MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle,} from '@angular/material/dialog';
import {MatFormField} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatListItemLine} from '@angular/material/list';
import {MatSelect, MatSelectTrigger} from '@angular/material/select';
import {MatSlider, MatSliderThumb} from '@angular/material/slider';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {AuthService} from '@app/auth/auth.service';
import {IdenticonPipe} from '@app/shared/identicon.pipe';
import {Role, User} from '@app/shared/user.service';
import {UserFacade} from "@shared/UserFacade";
import {Observable, of, tap} from 'rxjs';
import {catchError, mergeMap} from "rxjs/operators";
import {ColorSwatchKey, PreferencesService, SlackChannel, SortBy} from '../preferences.service';
import {SwatchPickerComponent} from '../swatch-picker/swatch-picker.component';

@Component({
    selector: 'frog-preferences-dialog',
    templateUrl: './preferences-dialog.component.html',
    styleUrls: ['./preferences-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        MatDialogContent,
        ReactiveFormsModule,
        MatTabGroup,
        MatTab,
        MatCheckbox,
        MatListItemLine,
        MatSlider,
        MatSliderThumb,
        MatButton,
        MatIcon,
        MatAnchor,
        MatFormField,
        MatSelect,
        MatSelectTrigger,
        MatOption,
        SwatchPickerComponent,
        MatDialogActions,
        MatDialogClose,
        AsyncPipe,
        TitleCasePipe,
        IdenticonPipe,
        MatIconButton,
    ],
})
export class PreferencesDialogComponent implements OnInit {
    public user$: Observable<User>;
    public preferences$: Observable<any>;
    public slackChannels$: Observable<SlackChannel[]>;
    public form: UntypedFormGroup;
    public slackChannelName: string;

    // enum access
    public SortBy = SortBy;
    public Role = Role;
    private slackChannels: SlackChannel[];

    constructor(
        private authService: AuthService,
        private preferencesService: PreferencesService,
        private userFacade: UserFacade,
        private matSnackBar: MatSnackBar,
        private formBuilder: UntypedFormBuilder,
    ) {
        this.form = this.formBuilder.group({
            sort_by: SortBy.Created,
            use_large_thumbnails: false,
            thumbnail_padding: 0,
            show_minimap: true,
            antialias: false,
            viewer_background: ColorSwatchKey.Black,
            notifications_likes: true,
            notifications_comments: true,
            notifications_mentions: true,
            notifications_via_email: true,
            notifications_via_slack: true,
            slack_channel: '',
        });
        this.slackChannels = [];
    }

    ngOnInit(): void {
        this.user$ = this.authService.user;
        this.preferences$ = this.preferencesService.preferences.pipe(
            tap((preferences) => {
                if (!preferences.slack?.installed) {
                    preferences.notifications_via_slack = false;
                }
                this.form.patchValue(preferences);
            }),
        );
        this.slackChannels$ = this.preferencesService.getSlackChannels()
            .pipe(
                catchError(err => {
                    this.matSnackBar.open(
                        `Failed to send message to slack: ${err.error.detail}`,
                        'Ok',
                        {
                            verticalPosition: 'top',
                            panelClass: 'error-snackbar',
                        },
                    );
                    return of([]);
                }),
                tap((channels) => {
                    this.slackChannels = channels;
                    this.setName(this.form.value.slack_channel);
                }),
            );
    }

    save() {
        this.preferencesService.setPreferences(this.form.value);
    }

    setName(channelId: string) {
        for (const channel of this.slackChannels) {
            if (channel.id === channelId) {
                this.slackChannelName = channel.name;
                break;
            }
        }
    }

    testSlack() {
        const slackChannelId = this.form.value.slack_channel;
        this.preferencesService.testSlack(slackChannelId).subscribe(
            (result) => {
                this.matSnackBar.open('Sent message to slack', undefined, {
                    duration: 2000,
                    verticalPosition: 'top',
                });
            },
            (error) => {
                this.matSnackBar.open(
                    `Failed to send message to slack: ${error.error.error}`,
                    'Ok',
                    {
                        verticalPosition: 'top',
                        panelClass: 'error-snackbar',
                    },
                );
            },
        );
    }

    uninstallSlack() {
        this.preferencesService.uninstallSlack().subscribe((result) => {
            this.preferencesService.getPreferences();
        });
    }

    userDetail(user: User) {
        this.userFacade.editUser(user)
            .pipe(
                mergeMap(() => this.authService.login(null))
            )
            .subscribe();
    }
}
